import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import DropZone from '@src/new-components/DropZone';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { COLOR } from '@src/styles/color';
import {
  AUDIO_ACCEPT,
  MAX_AUDIO_DURATION_LIMIT,
  MIN_AUDIO_DURATION_LIMIT,
} from '@src/constants/professionalVoiceCloneUpload';
import sanitize from 'sanitize-html';
import { Box } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { StyledAudioUploadSection } from './index.style';
import AudioEffectProgress from './AudioEffectProgress';
import AudioList from './AudioList';

const normalizeFiles = (files) => {
  if (!files) return [];
  if (Array.isArray(files)) return files;
  if (files instanceof FileList) return Array.from(files);
  return [files];
};

const AudioUploadSection = ({
  onFileUpload,
  progress,
  onOpenTips,
  audios,
  onDelete,
  onPlay,
  playingAudioId,
  isPlaying,
  onAdjust,
  isLoadingAudios,
}) => {
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const audiosWithStatus = audios.filter((audio) => !!audio?.status);
  const hasAudioStatus =
    audiosWithStatus?.length > 0 &&
    audiosWithStatus.every((audio) => !!audio?.status);

  const handleDelete = (id) => {
    if (onDelete) onDelete(id);
  };

  const handleDrop = async (files) => {
    const filesArray = normalizeFiles(files);

    const existingFileKeys = new Set(
      (audios || []).map((audio) => `${audio?.name || ''}-${audio?.size || 0}`),
    );

    const newFiles = filesArray.filter((file) => {
      const key = `${file?.name || ''}-${file?.size || 0}`;
      return !existingFileKeys.has(key);
    });

    if (!newFiles.length) return;

    if (onFileUpload) onFileUpload(newFiles);
  };

  const useTipsCreateVoice = getFeatureValue(FEATURE_KEYS.TIPS_CREATE_VOICE, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const renderTipsForBestResults = () => (
    <CommonButton
      onClick={onOpenTips}
      customStyle={{
        width: '100%',
        borderRadius: '12px',
        backgroundColor: COLOR.white,
        border: `1px solid ${COLOR.secondary[16]}`,
        padding: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '&:focus': {
          backgroundColor: COLOR.white,
        },
      }}
      variant="outlined"
      endIcon={
        <TipsAndUpdatesRoundedIcon sx={{ width: '32px', height: '32px' }} />
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        gap="4px"
      >
        <Typography variant="body-16-semibold" color={COLOR.darkGray}>
          {t('tipsForBestResults')}
        </Typography>
        <Typography
          variant="body-14-regular"
          style={{ textAlign: 'left' }}
          color={COLOR.label2[64]}
        >
          {t('tipsForBestResultsDescription')}
        </Typography>
      </Box>
    </CommonButton>
  );
  const renderProgressText = () => {
    if (progress < 30 && progress >= 0) {
      return t('progressTextDescription');
    }
    if (progress >= 30 && progress < 80) {
      return t('uploadMoreToImproveQuality');
    }
    if (progress >= 80) {
      return t('excellentQuality');
    }
    return '';
  };

  return (
    <StyledAudioUploadSection>
      <AudioEffectProgress
        progress={progress}
        hasAudioStatus={hasAudioStatus}
      />
      {hasAudioStatus && progress > 0 && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box display="flex" gap="8px" alignItems="center">
            <Typography
              variant="body-14-regular"
              color={COLOR.label2[64]}
              className="progress-text"
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  t('progressText', {
                    progress: progress.toFixed(1),
                    color:
                      progress < 30
                        ? COLOR.functionalRed.default
                        : COLOR.darkGray,
                  }),
                  {
                    allowedTags: ['b'],
                    allowedAttributes: {
                      b: ['style'],
                    },
                  },
                ),
              }}
            />
            {progress >= 30 && (
              <CheckCircleRoundedIcon
                sx={{ fontSize: '20px', color: COLOR.green }}
              />
            )}
          </Box>
          <Typography variant="body-14-regular" color={COLOR.label2[64]}>
            {renderProgressText()}
          </Typography>
        </Box>
      )}
      <DropZone
        accept={AUDIO_ACCEPT}
        height={200}
        validatorKeys={['format']}
        multiple
        onDrop={handleDrop}
        sx={{
          width: 'auto',
          height: 'auto',
          borderRadius: '12px',
          backgroundColor: COLOR.white,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <CommonButton
          variant="contained"
          startIcon={
            <CloudUploadRoundedIcon
              sx={{ fontSize: '24px !important', color: COLOR.white }}
            />
          }
          textProps={{
            variantText: 'body-14-semibold',
            color: COLOR.white,
          }}
          customStyle={{
            backgroundColor: COLOR.indigo.default,
            height: '40px',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: COLOR.indigo.default,
            },
            '&:active': {
              backgroundColor: `${COLOR.indigo.default}!important`,
            },
            '&:focus': {
              backgroundColor: `${COLOR.indigo.default}!important`,
            },
          }}
        >
          {t('uploadAudioSample')}
        </CommonButton>
        <Typography variant="body-14-regular" color={COLOR.label2[64]}>
          {t('onlySupportAudioAndCreateVoiceVietnamese', {
            seconds: MIN_AUDIO_DURATION_LIMIT,
            minutes: MAX_AUDIO_DURATION_LIMIT / 60,
          })}
        </Typography>
      </DropZone>
      {!hasAudioStatus &&
        useTipsCreateVoice &&
        !isLoadingAudios &&
        renderTipsForBestResults()}
      {!isLoadingAudios && (
        <AudioList
          audios={audios}
          onDelete={handleDelete}
          onPlay={onPlay}
          playingAudioId={playingAudioId}
          isPlaying={isPlaying}
          onAdjust={onAdjust}
        />
      )}
    </StyledAudioUploadSection>
  );
};

export default AudioUploadSection;
